'use client';

import { useState, useEffect } from 'react';
import { GalleryVerticalEnd, ArrowLeft } from "lucide-react"
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { useAuth } from '@/lib/auth';

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const { login } = useAuth();

  // Check if user was just verified or reset password
  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      setInfoMessage('Your email has been verified! Please log in.');
    } else if (searchParams.get('reset') === 'true') {
      setInfoMessage('Your password has been reset successfully! Please log in with your new password.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setInfoMessage('');

      // Use the unified login function that routes to merchants authentication
      await login(email, password);
    } catch (err: any) {
      // Check if this is an email_not_verified error
      if (err?.emailNotVerified) {
        setError('');
        setInfoMessage(err.message || 'Please verify your email first. A new verification code has been sent to your email.');
        // Optionally auto-redirect to verification page
        // router.push(`/verify-email?email=${encodeURIComponent(err.email)}`);
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("w-full", className)} {...props}>
      {/* Enhanced Form Container */}
      <div className="bg-white rounded-3xl shadow-2xl shadow-violet-500/20 border border-violet-100 p-6 backdrop-blur-sm bg-white/95">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-4">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <div className="flex flex-col items-center gap-2 text-center mb-5">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                  <span className="text-white font-black text-lg leading-none select-none" aria-hidden="true">
                    G
                  </span>
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                  GameIt.Lol
                </h1>
              </div>
              <h2 className="text-lg font-semibold text-gray-800">Welcome Back!</h2>
              <FieldDescription className="text-sm text-gray-600">
                Access your merchant dashboard and start gamifying customer experiences
              </FieldDescription>
            </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-xl mb-4">
              {error}
            </div>
          )}

          {infoMessage && (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm p-3 rounded-xl mb-4">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="mb-1 text-sm">{infoMessage}</p>
                  <Link
                    href={`/verify-email?email=${encodeURIComponent(email)}`}
                    className="inline-flex items-center gap-1 text-amber-800 font-medium hover:text-amber-900 transition-colors text-sm"
                  >
                    Go to Verification Page
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className={`space-y-4 transition-opacity duration-200 ${isLoading ? 'opacity-50 pointer-events-none select-none' : 'opacity-100'}`}>
            <Field>
              <FieldLabel htmlFor="email" className="text-gray-700 font-medium text-sm">Email Address</FieldLabel>
              <Input
                id="email"
                type="email"
                placeholder="merchant@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="h-10 border-gray-200 rounded-xl text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all disabled:bg-gray-50 disabled:text-gray-400"
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="password" className="text-gray-700 font-medium text-sm">Password</FieldLabel>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="h-10 border-gray-200 rounded-xl text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all disabled:bg-gray-50 disabled:text-gray-400"
              />
              <div className="flex justify-end mt-2">
                <Link
                  href="/forgot-password"
                  className="text-sm text-violet-600 hover:text-violet-700 transition-colors font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            </Field>
          </div>

          <Field className="mt-5">
            <Button
              type="submit"
              className="w-full h-10 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-xl hover:shadow-violet-500/30 transition-all duration-200 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Logging in...
                </div>
              ) : (
                'Login to Dashboard'
              )}
            </Button>
          </Field>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                href="/register"
                className="font-medium text-violet-600 hover:text-violet-700 transition-colors"
              >
                Create your free account
              </Link>
            </div>
          </div>
        </FieldGroup>
      </form>

      {/* Terms Section */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <FieldDescription className="text-center text-xs text-gray-500">
          By logging in, you agree to our{' '}
          <Link href="/terms" className="text-violet-600 hover:text-violet-700 transition-colors">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="/privacy" className="text-violet-600 hover:text-violet-700 transition-colors">
            Privacy Policy
          </Link>
        </FieldDescription>
      </div>
      </div>
    </div>
  )
}